import React from 'react';
import '../../../Css/Common/Modals/GlassConfirmationModal.css';
import { GlassModalHeader } from './GlassModalHeader';
import ModalPortal from './ModalPortal';

export interface GlassConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  messageContent?: React.ReactNode;
  warningMessage?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'danger';
  kicker?: string;
  isLoading?: boolean;
  loadingText?: string;
  confirmDisabled?: boolean;
}

export const GlassConfirmationModal: React.FC<GlassConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  messageContent,
  warningMessage,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  kicker = 'Confirm action',
  isLoading = false,
  loadingText = 'Processing…',
  confirmDisabled = false,
}) => {
  if (!isOpen) {
    return null;
  }

  const shellClass =
    variant === 'danger'
      ? 'glassConfirmationModal_container glassModal_shell glassModal_shell--danger'
      : 'glassConfirmationModal_container glassModal_shell glassModal_shell--account';

  return (
    <ModalPortal modalRootId="generalModal_fullScreenScrim" onClose={onClose}>
      <div
        className={shellClass}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <GlassModalHeader
          kicker={kicker}
          title={title}
          variant={variant === 'danger' ? 'danger' : 'default'}
          onClose={onClose}
          closeAriaLabel="Close confirmation"
        />

        <div className="glassConfirmationModal_body">
          <div
            className={`glassConfirmationModal_callout${
              variant === 'danger' ? ' glassConfirmationModal_callout--danger' : ''
            }`}
          >
            <div className="glassConfirmationModal_calloutIconWrap" aria-hidden>
              <span
                className={`fa-solid ${
                  variant === 'danger' ? 'fa-triangle-exclamation' : 'fa-circle-info'
                } glassConfirmationModal_calloutIcon`}
              />
            </div>
            <div className="glassConfirmationModal_calloutBody">
              {messageContent ?? (
                <p className="glassConfirmationModal_message">{message}</p>
              )}
              {!messageContent && warningMessage ? (
                <p className="glassConfirmationModal_warning">{warningMessage}</p>
              ) : null}
            </div>
          </div>

          <div className="glassConfirmationModal_footer">
            <button
              type="button"
              className="glassConfirmationModal_cancelButton"
              onClick={onClose}
              disabled={isLoading}
            >
              {cancelText}
            </button>
            <button
              type="button"
              className={`glassConfirmationModal_confirmButton${
                variant === 'danger' ? ' glassConfirmationModal_confirmButton--danger' : ''
              }`}
              onClick={onConfirm}
              disabled={isLoading || confirmDisabled}
            >
              {isLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  {loadingText}
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default GlassConfirmationModal;
